import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Container, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Preloader from "../../shared/PreLoaders";
import { AuthContext } from "../../Context/AuthContext";
import TableComponent from "../../utils/TableComponent";
import { logActivity } from "../../utils/LogActivity";
import { Authorization } from "../../Context/Authorization";

const ManageMember = () => {
  const [manageData, setManageData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { permissions, userId } = useContext(AuthContext);
  const [range, setRange] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedRange, setSelectedRange] = useState("");
  const [selectedVfc, setSelectedVfc] = useState("");
  const [vfcs, setVfcs] = useState([]);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    // Fetch Ranges
    const fetchRange = async () => {
      setLoading(true);
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/ranges`, config);
        const data = response.data;
        setRange(data);
      } catch (err) {
        console.error("Error fetching ranges", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRange();
  }, [Api]);

  useEffect(() => {
    // Fetch VFCs based on selected range
    const fetchVfc = async () => {
      if (!selectedRange) return;

      setLoading(true);
      try {
        const config = Authorization();
        const response = await axios.post(
          `${Api}/api/vfcs/vfcs-by-range`,
          {
            rangeId: selectedRange,
          },
          config
        );
        const data = response.data.vfcs;
        setVfcs(data);
        setErrorMessage("");
      } catch (err) {
        console.error("Error fetching VFCs", err);
        setVfcs([]);
        setErrorMessage("No VFC available.");
      } finally {
        setLoading(false);
      }
    };

    fetchVfc();
  }, [Api, selectedRange]);

  useEffect(() => {
    // Fetch Members based on selected VFC
    const fetchMembers = async () => {
      if (!selectedVfc) return;

      setLoading(true);
      try {
        const config = Authorization();
        const response = await axios.get(
          `${Api}/api/members/getMembersByVFC/${selectedVfc}`,
          config
        );
        setManageData(response.data.members || []);
        await logActivity(userId, "Viewed Member", "Member Page", Api);
      } catch (error) {
        console.error("Error fetching members", error);
        setManageData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [selectedVfc]);

  const handleDeleteState = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this Member?"
    );
    if (!confirmDelete) {
      return;
    }
    try {
      await axios.delete(`${Api}/api/members/${id}`);
      setManageData(manageData.filter((state) => state._id !== id));
      await logActivity(userId, "Deleted Member", "Member Page", Api);
      toast.success("Deleted Successfully");
    } catch (err) {
      console.error("Error deleting Member", err);
      toast.error("Failed to delete Member");
    }
  };
  const handleEditMember = (id) => {
    navigate(`/member/manage/edit/${id}`);
  };
  const handleAddMember = () => {
    navigate("member/add");
  };

  const columns = [
    {
      headerName: "Action",
      field: "action",
      cellRenderer: (params) => {
        return (
          <div>
            {permissions.includes("EditMember") && (
              <i
                className="mdi mdi-pen"
                style={{ fontSize: "1.5rem", cursor: "pointer" }}
                onClick={() => handleEditMember(params.data._id)}
              />
            )}
            {permissions.includes("DeleteMember") && (
              <i
                className="mdi mdi-delete"
                style={{ fontSize: "1.5rem", cursor: "pointer" }}
                onClick={() => handleDeleteState(params.data._id)}
              />
            )}
          </div>
        );
      },
    },
    {
      headerName: "SI.No",
      field: "index",
      valueGetter: (params) => params.node.rowIndex + 1,
    },
    {
      headerName: "Name",
      field: "name",
    },

    {
      headerName: "Age",
      field: "age",
    },
    {
      headerName: "Gender",
      field: "gender",
    },
    {
      headerName: "Vfc Code",
      field: "vfcCode.vfcName",
    },
    {
      headerName: "Father or Husband",
      field: "fatherOrHusband",
    },
    {
      headerName: "Father or Husband Name",
      field: "fatherOrHusbandName",
    },
    {
      headerName: "Address",
      field: "address",
    },
    {
      headerName: "Village",
      field: "village",
    },
    {
      headerName: "Panchayath",
      field: "panchayath",
    },
    {
      headerName: "Thaluk",
      field: "thaluk",
    },
    {
      headerName: "Pin Code",
      field: "pinCode",
    },
    {
      headerName: "District",
      field: "district",
    },
    {
      headerName: "Date of Birth",
      field: "dob",
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      headerName: "Phone",
      field: "phone",
    },
    {
      headerName: "Aadhar",
      field: "aadhar",
    },
    {
      headerName: "Ration Card",
      field: "rationCard",
    },
    {
      headerName: "Bank",
      field: "bank",
    },
    {
      headerName: "Branch",
      field: "branch",
    },
    {
      headerName: "A/c No",
      field: "accountNumber",
    },
    {
      headerName: "Email",
      field: "eMail",
    },
    {
      headerName: "Community",
      field: "community",
    },
    {
      headerName: "IFSC Code",
      field: "ifscCode",
    },
    {
      headerName: "Occupation",
      field: "work",
    },
    {
      headerName: "Education",
      field: "educationalQualification",
    },
    {
      headerName: "Income",
      field: "income",
    },
    {
      headerName: "Whatsapp No",
      field: "whatsAppNo",
    },
    {
      headerName: "Maritial Status",
      field: "maritialStatus",
    },
    {
      headerName: "Family Details",
      field: "familyMemberDetails",
    },
  ];

  return (
    <div>
      <Preloader loading={loading} />

      <div className="page-header">
        <h3 className="page-title">VFC Members</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              VFC Members
            </li>
          </ol>
        </nav>
      </div>
      <Container fluid className="mt-5">
        <div>
          {errorMessage && (
            <div className="alert alert-danger mt-3" role="alert">
              {errorMessage}
            </div>
          )}
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <form>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group>
                        <label htmlFor="name">Range</label>
                        <Form.Control
                          as="select"
                          id="name"
                          value={selectedRange}
                          onChange={(e) => setSelectedRange(e.target.value)}
                        >
                          <option>Select Range</option>
                          {range.map((range) => (
                            <option key={range._id} value={range._id}>
                              {range.rangeName}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group>
                        <label htmlFor="name">VFC</label>
                        <Form.Control
                          as="select"
                          id="name"
                          value={selectedVfc}
                          onChange={(e) => setSelectedVfc(e.target.value)}
                          disabled={!selectedRange}
                        >
                          <option value="">Select VFC</option>
                          {vfcs.map((vfc) => (
                            <option key={vfc._id} value={vfc._id}>
                              {vfc.vfcName}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="row icons-list">
                {permissions.includes("AddMember") && (
                  <div className="Add_btn">
                    <button
                      type="button"
                      className="btn btn-inverse-success"
                      onClick={handleAddMember}
                    >
                      Create New
                    </button>
                  </div>
                )}
                <div className="table-responsive">
                  <TableComponent data={manageData} columns={columns} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ManageMember;
